import React, { useRef } from 'react';
import * as THREE from 'three';
import { motion } from 'framer-motion';
import { ArrowRight, Download, Terminal, Code2, Database } from 'lucide-react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, MeshDistortMaterial, Sphere, Stars, TorusKnot } from '@react-three/drei';
import ErrorBoundary from './ErrorBoundary';

const InteractiveScene = () => {
  const groupRef = useRef();
  const sphereRef1 = useRef();
  const sphereRef2 = useRef();

  useFrame((state) => {
    if (!groupRef.current) return;
    const { pointer } = state;
    
    // Smooth lerp rotation toward cursor
    groupRef.current.rotation.x = THREE.MathUtils.lerp(groupRef.current.rotation.x, -pointer.y * 0.25, 0.05);
    groupRef.current.rotation.y = THREE.MathUtils.lerp(groupRef.current.rotation.y, pointer.x * 0.35, 0.05);
    
    // Parallax position shift
    groupRef.current.position.x = THREE.MathUtils.lerp(groupRef.current.position.x, pointer.x * 0.4, 0.05);
    groupRef.current.position.y = THREE.MathUtils.lerp(groupRef.current.position.y, pointer.y * 0.3, 0.05);

    // Subtle counter-rotation for individual elements
    if (sphereRef1.current) {
      sphereRef1.current.rotation.y += 0.005;
    }
    if (sphereRef2.current) {
      sphereRef2.current.rotation.x += 0.004;
    }
  });

  return (
    <group ref={groupRef}>
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 10, 5]} intensity={1.2} />
      <pointLight position={[-10, -10, -5]} intensity={0.5} color="#3b82f6" />
      
      {/* Primary Floating Orb */}
      <Float speed={2} rotationIntensity={1.2} floatIntensity={1.8}>
        <Sphere ref={sphereRef1} args={[1.1, 64, 64]} position={[-2, 1.2, -4]} scale={1.2}>
          <MeshDistortMaterial 
            color="#3b82f6" 
            attach="material" 
            distort={0.45} 
            speed={2.2} 
            roughness={0.25} 
            transparent
            opacity={0.35}
          />
        </Sphere>
      </Float>

      {/* Secondary Floating Orb */}
      <Float speed={1.8} rotationIntensity={1.8} floatIntensity={2}>
        <Sphere ref={sphereRef2} args={[1, 64, 64]} position={[2.2, -1.2, -5]} scale={1.3}>
          <MeshDistortMaterial 
            color="#10b981" 
            attach="material" 
            distort={0.55} 
            speed={1.8} 
            roughness={0.2}
            transparent
            opacity={0.3}
          />
        </Sphere>
      </Float>

      {/* Center Subtle Cyber Torus Ring */}
      <Float speed={1.2} rotationIntensity={0.8} floatIntensity={1.2}>
        <TorusKnot args={[1.8, 0.06, 128, 16, 2, 3]} position={[0, 0, -7]}>
          <meshStandardMaterial 
            color="#6366f1" 
            wireframe 
            transparent 
            opacity={0.15} 
          />
        </TorusKnot>
      </Float>

      {/* Ambient Stardust */}
      <Stars radius={90} depth={45} count={2200} factor={4} saturation={0} fade speed={1.2} />
    </group>
  );
};

const Hero = ({ onOpenCommandPalette }) => {
  return (
    <section id="home" className="min-h-screen flex items-center justify-center pt-24 pb-12 relative overflow-hidden bg-background dark:bg-background-dark">
      {/* 3D Interactive Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <ErrorBoundary fallback={<div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent pointer-events-none" />}>
          <Canvas camera={{ position: [0, 0, 5], fov: 45 }}>
            <InteractiveScene />
          </Canvas>
        </ErrorBoundary>
      </div>
      
      <div className="container mx-auto px-4 max-w-5xl relative z-10 flex flex-col items-center text-center">
        
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-3 px-3 py-1.5 pr-5 rounded-full bg-slate-100/80 dark:bg-[#111]/80 backdrop-blur-md border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200 text-sm font-medium hover:border-sky-500/40 transition-all duration-300 cursor-default shadow-sm group">
            <img 
              src="/assets/profile.jpg" 
              alt="Ravindran Sanjeyan" 
              className="w-7 h-7 rounded-full object-cover ring-2 ring-emerald-500/30"
            />
            <span>Hello, I'm Ravindran Sanjeyan</span>
            <span className="relative flex h-2 w-2 ml-1">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <h1 className="text-6xl md:text-8xl font-bold font-display leading-[1.05] tracking-tighter mb-8 max-w-4xl mx-auto bg-clip-text text-transparent bg-gradient-to-b from-slate-900 via-slate-800 to-slate-500 dark:from-white dark:via-white dark:to-slate-300 pb-2">
            Engineering digital <br className="hidden md:block"/>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-indigo-400 to-emerald-400">experiences.</span>
          </h1>
        </motion.div>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-lg md:text-xl text-slate-600 dark:text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed font-light"
        >
          A Full Stack Developer focused on creating elegant, efficient, and highly scalable software solutions. Merging modern architecture with world-class minimalist design.
        </motion.p>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3.5 w-full sm:w-auto"
        >
          <a href="#projects" className="w-full sm:w-auto px-7 py-3.5 bg-black dark:bg-white text-white dark:text-black hover:bg-slate-800 dark:hover:bg-slate-200 rounded-2xl font-medium transition-colors flex items-center justify-center gap-2 group shadow-lg shadow-black/10 dark:shadow-white/10 text-base">
            View Projects
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <button 
            onClick={onOpenCommandPalette}
            className="w-full sm:w-auto px-6 py-3.5 bg-white/60 dark:bg-[#111]/60 backdrop-blur-xl hover:bg-slate-50 dark:hover:bg-[#1c1c1c] text-black dark:text-white border border-slate-200 dark:border-[#333] rounded-2xl font-medium transition-all flex items-center justify-center gap-2.5 shadow-sm text-base group"
          >
            <Terminal size={17} className="text-emerald-500" />
            <span>Command Bar</span>
            <kbd className="hidden sm:inline-block px-1.5 py-0.5 rounded bg-slate-100 dark:bg-[#222] text-[10px] text-slate-500 dark:text-slate-400 font-mono border border-slate-200 dark:border-[#333]">⌘K</kbd>
          </button>
          <a 
            href="/assets/cv.pdf" 
            target="_blank" 
            rel="noopener noreferrer" 
            download="Ravindran_Sanjeyan_CV.pdf"
            className="w-full sm:w-auto px-6 py-3.5 bg-transparent hover:bg-slate-100 dark:hover:bg-[#161616] text-slate-700 dark:text-slate-300 border border-transparent hover:border-slate-200 dark:hover:border-[#333] rounded-2xl font-medium transition-colors flex items-center justify-center gap-2 text-base"
          >
            <Download size={18} />
            Download CV
          </a>
        </motion.div>

        {/* Floating tech background elements */}
        <motion.div 
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 -left-12 md:left-0 hidden lg:flex items-center justify-center w-16 h-16 rounded-2xl bg-white/60 dark:bg-[#111]/60 backdrop-blur-xl border border-slate-100 dark:border-[#222] shadow-sm text-slate-400 dark:text-slate-500"
        >
          <Code2 size={24} />
        </motion.div>
        
        <motion.div 
          animate={{ y: [0, 15, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-1/4 -right-12 md:right-0 hidden lg:flex items-center justify-center w-16 h-16 rounded-2xl bg-white/60 dark:bg-[#111]/60 backdrop-blur-xl border border-slate-100 dark:border-[#222] shadow-sm text-slate-400 dark:text-slate-500 hover:scale-110 transition-transform"
        >
          <Terminal size={24} />
        </motion.div>
        
      </div>
    </section>
  );
};

export default Hero;
